import { useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { NodeAvatar, Pill } from "../primitives";
import { PanelRow } from "../cockpit/Panel";
import { EntityChip } from "./EntityChip";
import { useEntityTypes } from "../../../lib/hooks/use-kuvio";
import { formatRelativeTime } from "../../lib/v3-columns";
import type { RowBackReferences } from "../../../lib/types";

/**
 * Sprint 12 — everything that points AT this row, grouped by referrer type:
 * Memories, Pages, Tasks, Communication (Sprint 13 reservation), then other
 * entity tables (alphabetical). Each section is collapsible with a count badge;
 * non-empty sections start open. Read-only from this side — clicking an item
 * navigates (pages/memories/tasks) or opens the record's panel (entity rows).
 */

interface BackReferencesProps {
  backRefs: RowBackReferences;
  onOpenRef?: (entityTypeSlug: string, entityId: string) => void;
}

export function BackReferences({ backRefs, onOpenRef }: BackReferencesProps) {
  const navigate = useNavigate();
  const entityTypes = useEntityTypes();

  const typeName = (slug: string): string => {
    const def = entityTypes.data?.find((t) => t.slug === slug);
    if (!def) return slug;
    return def.plural ?? `${def.name}s`;
  };

  const entityGroups = Object.entries(backRefs.entity_back_refs).sort(([a], [b]) =>
    a.localeCompare(b),
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <Section title="Memories" count={backRefs.memories.length}>
        {backRefs.memories.map((m) => (
          <PanelRow
            key={m.id}
            onClick={() => navigate(`/library/${m.id}`)}
            title={m.name}
            trailing={<span style={metaStyle}>{m.memory_type ?? "memory"} · {formatRelativeTime(m.updated_at)}</span>}
          >
            <NodeAvatar type="memory" size="sm" name={m.name} />
            <span style={nameStyle}>{m.name}</span>
          </PanelRow>
        ))}
      </Section>

      <Section title="Pages" count={backRefs.pages.length}>
        {backRefs.pages.map((p) => (
          <PanelRow
            key={p.id}
            onClick={() => navigate(`/library/${p.id}`)}
            title={p.name}
            trailing={<span style={metaStyle}>{[p.surface, p.status].filter(Boolean).join(" · ") || formatRelativeTime(p.updated_at)}</span>}
          >
            <NodeAvatar type="page" size="sm" name={p.name} />
            <span style={nameStyle}>{p.name}</span>
          </PanelRow>
        ))}
      </Section>

      <Section title="Tasks" count={backRefs.tasks.length}>
        {backRefs.tasks.map((t) => (
          <PanelRow
            key={t.id}
            onClick={() => navigate(`/agents/${t.agent_id}`)}
            title={t.title}
            trailing={
              <span style={metaStyle}>
                {t.status.replace(/_/g, " ")}
                {t.due_by ? ` · due ` : ""}              </span>
            }
          >
            <i className="ti ti-checkbox" style={{ fontSize: 15, color: "var(--color-text-tertiary)", flexShrink: 0 }} aria-hidden="true" />
            <span style={nameStyle}>{t.title}</span>
          </PanelRow>
        ))}
      </Section>

      {entityGroups.map(([slug, items]) => (
        <Section key={slug} title={typeName(slug)} count={items.length}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "4px 8px" }}>
            {items.map((it) => (
              <EntityChip key={it.id} id={it.id} name={it.name} typeSlug={slug} onOpen={onOpenRef} />
            ))}
          </div>
        </Section>
      ))}
    </div>
  );
}

function Section({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  const [open, setOpen] = useState(count > 0);
  return (
    <div>
      <button onClick={() => setOpen((v) => !v)} style={headerStyle} aria-expanded={open}>
        <i className={`ti ti-chevron-${open ? "down" : "right"}`} style={{ fontSize: 13, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
        <span>{title}</span>
        <Pill variant="gray">{count}</Pill>
      </button>
      {open && (
        <div style={{ paddingLeft: 2 }}>
          {count === 0 ? (
            <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", padding: "4px 8px" }}>None.</div>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );
}

const headerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  width: "100%",
  padding: "6px 4px",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  textAlign: "left",
};

const nameStyle: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const metaStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  whiteSpace: "nowrap",
};
